import { useEffect, useRef, useState } from "react";
import api from "@/api/client";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { BookUser, Plus, Search, Trash2, Pencil, Building2, MapPin, Truck, Upload, FileDown, AlertTriangle, CheckCircle2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import MasterTable from "@/components/MasterTable";

const EMPTY_FORM = { name: "", employee_id: "", pincode: "", department: "", phone: "", email: "", location: "", branch_id: "", company_id: "", department_id: "", default_route_id: "" };

export default function EmployeesPage() {
  const { user, tenant } = useAuth();
  const [items, setItems] = useState([]);
  const [branches, setBranches] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({ id: "", ...EMPTY_FORM });
  const [importOpen, setImportOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importPreview, setImportPreview] = useState([]);
  const [importAllRows, setImportAllRows] = useState([]);
  const [importResult, setImportResult] = useState(null);
  const [importBusy, setImportBusy] = useState(false);
  const importFileRef = useRef(null);

  const SAMPLE_CSV = "Name,Email,Employee ID,Department,Company,Branch,Phone,Pincode,Active\nJane Doe,jane@acme.io,EMP001,Engineering,Default,HQ,9990001111,400001,true\n";
  const downloadSample = () => {
    const blob = new Blob([SAMPLE_CSV], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "employees-template.csv"; a.click();
    URL.revokeObjectURL(url);
  };
  const parseCsvPreview = (text) => {
    const lines = (text || "").split(/\r?\n/).filter((l) => l.trim());
    if (lines.length < 2) return { headers: [], rows: [] };
    const headers = lines[0].split(",").map((h) => h.trim());
    const rows = lines.slice(1).map((ln) => {
      const cells = ln.split(",");
      const row = {};
      headers.forEach((h, i) => { row[h] = (cells[i] || "").trim(); });
      return row;
    });
    return { headers, rows };
  };
  const onFileChosen = async (f) => {
    setImportFile(f); setImportResult(null); setImportPreview([]); setImportAllRows([]);
    if (!f) return;
    const text = await f.text();
    const { rows } = parseCsvPreview(text);
    setImportAllRows(rows);
    setImportPreview(rows.slice(0, 5));
  };
  const downloadErrorsCsv = () => {
    if (!importResult?.errors?.length) return;
    // Echo original headers + append Row# and Errors so operators fix + re-upload
    const headers = importAllRows[0] ? Object.keys(importAllRows[0]) : [];
    const outHeaders = ["Row", ...headers, "Errors"];
    const escape = (s) => {
      const v = String(s ?? "");
      return /[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
    };
    const lines = [outHeaders.map(escape).join(",")];
    importResult.errors.forEach((e) => {
      // e.row is 2-based (row 2 = first data row = importAllRows[0])
      const src = importAllRows[e.row - 2] || {};
      const cells = [e.row, ...headers.map((h) => src[h] || ""), e.errors.join(" · ")];
      lines.push(cells.map(escape).join(","));
    });
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "employees-import-errors.csv"; a.click();
    URL.revokeObjectURL(url);
  };
  const doImport = async () => {
    if (!importFile) return toast.error("Choose a CSV file first");
    setImportBusy(true);
    try {
      const fd = new FormData(); fd.append("file", importFile);
      const { data } = await api.post("/recipients/bulk", fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      setImportResult(data);
      toast.success(`Imported ${data.imported}/${data.total_rows} rows`,
        { description: data.error_count ? `${data.error_count} row(s) had errors — see details` : "All rows imported cleanly" });
      if (data.imported > 0) load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Import failed"); }
    finally { setImportBusy(false); }
  };

  const load = async () => {
    const [rec, br, co, dep, rt] = await Promise.all([
      api.get("/recipients", { params: { q: q || undefined } }),
      api.get("/branches").catch(() => ({ data: [] })),
      api.get("/companies").catch(() => ({ data: [] })),
      api.get("/departments").catch(() => ({ data: [] })),
      api.get("/routes", { params: { status: "active" } }).catch(() => ({ data: [] })),
    ]);
    setItems(rec.data);
    setBranches(br.data || []);
    setCompanies(co.data || []);
    setDepartments(dep.data || []);
    setRoutes(rt.data || []);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/recipients", { ...form, branch_id: form.branch_id || null });
      toast.success("Employee added");
      setOpen(false);
      setForm(EMPTY_FORM);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  // Feb 10, 2026 — refresh masters on dialog-open so newly-created Companies /
  // Departments / Routes show up in the dropdowns without a page reload.
  const openCreate = () => { load(); setForm(EMPTY_FORM); setOpen(true); };

  const openEdit = (r) => {
    load(); // Refresh masters so latest Companies / Departments / Routes appear.
    setEditForm({
      id: r.id,
      name: r.name || "",
      employee_id: r.employee_id || "",
      pincode: r.pincode || "",
      department: r.department || "",
      phone: r.phone || "",
      email: r.email || "",
      location: r.location || "",
      branch_id: r.branch_id || "",
      company_id: r.company_id || "",
      department_id: r.department_id || "",
      default_route_id: r.default_route_id || "",
    });
    setEditOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      const { id, ...body } = editForm;
      await api.patch(`/recipients/${id}`, { ...body, branch_id: body.branch_id || null });
      toast.success("Employee updated");
      setEditOpen(false);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this employee?")) return;
    await api.delete(`/recipients/${id}`);
    toast.success("Removed");
    load();
  };

  const canDelete = user?.role === "company_admin" || user?.role === "branch_admin";
  const canEdit = ["company_admin", "branch_admin", "branch_user"].includes(user?.role);
  const branchName = (bid) => branches.find((b) => b.id === bid)?.name || "—";

  return (
    <div className="p-8 space-y-6">
      <header className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="overline flex items-center gap-2"><BookUser size={12}/> Corporate directory</div>
          <h1 className="text-4xl font-display mt-1">Employees.</h1>
          <p className="text-sm text-muted-foreground mt-1">Every parcel is routed to a real person. Add them once, reuse forever.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input data-testid="emp-search" value={q} onChange={(e)=>setQ(e.target.value)} onKeyDown={(e)=>{if(e.key==="Enter") load();}}
              className="pl-8 rounded-md border-input w-64" placeholder="Search name, dept, email…" />
          </div>
          <Button data-testid="emp-search-btn" onClick={load} className="rounded-md bg-foreground text-background hover-brut shadow-brut">Search</Button>
          <Button data-testid="emp-import-btn" onClick={() => setImportOpen(true)}
            variant="outline" className="rounded-md">
            <Upload size={14} className="mr-2"/> Import
          </Button>
          <Button data-testid="emp-new-btn" onClick={openCreate} className="rounded-md bg-primary text-primary-foreground hover-brut shadow-brut">
            <Plus size={14} className="mr-2"/> Add employee
          </Button>
        </div>
      </header>

      <MasterTable
        columns={[
          { key: "name", label: "Name", render: (r) => <span className="font-semibold">{r.name}</span> },
          { key: "employee_id", label: "Emp. ID", className: "mono text-xs", render: (r) => r.employee_id || "—" },
          { key: "department", label: "Department", className: "text-muted-foreground",
            render: (r) => r.department || "—" },
          { key: "pincode", label: "Pincode", className: "mono text-xs", render: (r) => r.pincode || "—" },
          { key: "company", label: "Company", className: "text-muted-foreground text-xs", sortable: false,
            render: () => tenant?.name || "—" },
          { key: "branch_name", label: "Branch", className: "text-muted-foreground text-xs" },
          { key: "location", label: "Location", className: "text-muted-foreground text-xs",
            render: (r) => r.location || "—" },
          { key: "phone", label: "Phone", className: "mono text-xs", render: (r) => r.phone || "—" },
          { key: "email", label: "Email", className: "mono text-xs", render: (r) => r.email || "—" },
          { key: "actions", label: "", sortable: false, className: "text-right whitespace-nowrap", render: (r) => (
              <div className="inline-flex items-center gap-3">
                {canEdit && (
                  <button data-testid={`emp-edit-${r.id}`} onClick={() => openEdit(r)} className="text-muted-foreground hover:text-primary">
                    <Pencil size={14} />
                  </button>
                )}
                {canDelete && (
                  <button data-testid={`emp-delete-${r.id}`} onClick={() => remove(r.id)} className="text-destructive hover:text-primary">
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
          )},
        ]}
        rows={items.map((r) => ({ ...r, branch_name: branchName(r.branch_id) }))}
        searchKeys={["name", "department", "phone", "email", "location", "branch_name"]}
        getRowKey={(r) => r.id}
        testidPrefix="emp"
        defaultSort={{ key: "name", dir: "asc" }}
        emptyText="No employees match your filters."
      />

      {/* Create */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-md max-w-lg" data-testid="emp-create-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl">Add employee</DialogTitle></DialogHeader>
          <form onSubmit={create} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="overline flex items-center gap-1.5"><Building2 size={11} /> Company</span>
                <select data-testid="emp-company" value={form.company_id || ""}
                        onChange={(e)=>setForm({...form, company_id: e.target.value, department_id: ""})}
                        className="mt-1 w-full h-10 px-3 rounded-md border border-input bg-background text-sm">
                  <option value="">— unassigned —</option>
                  {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </label>
              <label className="block">
                <span className="overline flex items-center gap-1.5"><Building2 size={11} /> Department</span>
                <select data-testid="emp-department-id" value={form.department_id || ""}
                        onChange={(e)=>setForm({...form, department_id: e.target.value})}
                        disabled={!form.company_id}
                        className="mt-1 w-full h-10 px-3 rounded-md border border-input bg-background text-sm disabled:bg-muted disabled:cursor-not-allowed">
                  <option value="">— unassigned —</option>
                  {departments.filter(d => d.company_id === form.company_id).map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                </select>
              </label>
            </div>
            <F label="Name" v={form.name} onChange={v=>setForm({...form, name:v})} required t="emp-name"/>
            <div className="grid grid-cols-2 gap-3">
              <F label="Employee ID" v={form.employee_id} onChange={v=>setForm({...form, employee_id:v})} required t="emp-employee-id"/>
              <F label="Pincode (6-digit)" v={form.pincode} onChange={v=>setForm({...form, pincode:v.replace(/\D/g,"").slice(0,6)})} required t="emp-pincode"/>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <F label="Legacy dept label" v={form.department} onChange={v=>setForm({...form, department:v})} t="emp-dept"/>
              <F label="Phone" v={form.phone} onChange={v=>setForm({...form, phone:v})} t="emp-phone"/>
            </div>
            <F label="Email" v={form.email} onChange={v=>setForm({...form, email:v})} type="email" t="emp-email"/>
            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="overline flex items-center gap-1.5"><Building2 size={11} /> Location</span>
                <Input data-testid="emp-location" value={form.location} onChange={e=>setForm({...form, location:e.target.value})}
                  placeholder="Floor / Desk / Wing" className="mt-1 rounded-md" />
              </label>
              <label className="block">
                <span className="overline flex items-center gap-1.5"><Truck size={11} /> Default Route</span>
                <select data-testid="emp-default-route" value={form.default_route_id || ""}
                        onChange={(e)=>setForm({...form, default_route_id: e.target.value})}
                        className="mt-1 w-full h-10 px-3 rounded-md border border-input bg-background text-sm">
                  <option value="">— none —</option>
                  {routes.map(r => <option key={r.id} value={r.id}>{r.name} ({r.code})</option>)}
                </select>
              </label>
            </div>
            <div className="border border-dashed border-border rounded-md p-2 text-[11px] text-muted-foreground bg-muted/30">
              <span className="overline">Company:</span> <span className="ml-1 font-semibold text-foreground">{tenant?.name || "—"}</span>
              <span className="mono ml-2">{tenant?.code ? `· ${tenant.code}` : ""}</span>
            </div>
            <Button type="submit" data-testid="emp-submit" className="w-full rounded-md bg-primary text-primary-foreground hover-brut shadow-brut">Save</Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="rounded-md max-w-lg" data-testid="emp-edit-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl">Edit employee</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="overline flex items-center gap-1.5"><Building2 size={11} /> Company</span>
                <select data-testid="emp-edit-company" value={editForm.company_id || ""}
                        onChange={(e)=>setEditForm({...editForm, company_id: e.target.value, department_id: ""})}
                        className="mt-1 w-full h-10 px-3 rounded-md border border-input bg-background text-sm">
                  <option value="">— unassigned —</option>
                  {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </label>
              <label className="block">
                <span className="overline flex items-center gap-1.5"><Building2 size={11} /> Department</span>
                <select data-testid="emp-edit-department-id" value={editForm.department_id || ""}
                        onChange={(e)=>setEditForm({...editForm, department_id: e.target.value})}
                        disabled={!editForm.company_id}
                        className="mt-1 w-full h-10 px-3 rounded-md border border-input bg-background text-sm disabled:bg-muted">
                  <option value="">— unassigned —</option>
                  {departments.filter(d => d.company_id === editForm.company_id).map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                </select>
              </label>
            </div>
            <F label="Name" v={editForm.name} onChange={v=>setEditForm({...editForm, name:v})} required t="emp-edit-name"/>
            <div className="grid grid-cols-2 gap-3">
              <F label="Employee ID" v={editForm.employee_id} onChange={v=>setEditForm({...editForm, employee_id:v})} required t="emp-edit-employee-id"/>
              <F label="Pincode (6-digit)" v={editForm.pincode} onChange={v=>setEditForm({...editForm, pincode:v.replace(/\D/g,"").slice(0,6)})} required t="emp-edit-pincode"/>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <F label="Legacy dept label" v={editForm.department} onChange={v=>setEditForm({...editForm, department:v})} t="emp-edit-dept"/>
              <F label="Phone" v={editForm.phone} onChange={v=>setEditForm({...editForm, phone:v})} t="emp-edit-phone"/>
            </div>
            <F label="Email" v={editForm.email} onChange={v=>setEditForm({...editForm, email:v})} type="email" t="emp-edit-email"/>
            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="overline flex items-center gap-1.5"><MapPin size={11} /> Location</span>
                <Input data-testid="emp-edit-location" value={editForm.location} onChange={e=>setEditForm({...editForm, location:e.target.value})}
                  placeholder="Floor / Desk / Wing" className="mt-1 rounded-md" />
              </label>
              <label className="block">
                <span className="overline flex items-center gap-1.5"><Truck size={11} /> Default Route</span>
                <select data-testid="emp-edit-default-route" value={editForm.default_route_id || ""}
                        onChange={(e)=>setEditForm({...editForm, default_route_id: e.target.value})}
                        className="mt-1 w-full h-10 px-3 rounded-md border border-input bg-background text-sm">
                  <option value="">— none —</option>
                  {routes.map(r => <option key={r.id} value={r.id}>{r.name} ({r.code})</option>)}
                </select>
              </label>
              <label className="block">
                <span className="overline flex items-center gap-1.5"><Building2 size={11} /> Branch</span>
                <select data-testid="emp-edit-branch" value={editForm.branch_id || ""}
                  onChange={(e)=>setEditForm({...editForm, branch_id: e.target.value})}
                  disabled={user?.role !== "company_admin" && user?.role !== "super_admin"}
                  className="mt-1 w-full h-10 px-3 rounded-md border border-input bg-background text-sm focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none disabled:bg-muted">
                  <option value="">— pick a branch —</option>
                  {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
              </label>
            </div>
            <div className="border border-dashed border-border rounded-md p-2 text-[11px] text-muted-foreground bg-muted/30">
              <span className="overline">Company:</span> <span className="ml-1 font-semibold text-foreground">{tenant?.name || "—"}</span>
              <span className="mono ml-2">{tenant?.code ? `· ${tenant.code}` : ""}</span>
            </div>
            <Button type="submit" data-testid="emp-edit-submit" className="w-full rounded-md bg-primary text-primary-foreground hover-brut shadow-brut">Save changes</Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Bulk Import dialog (Feb 12, 2026) */}
      <Dialog open={importOpen} onOpenChange={(v) => { setImportOpen(v); if (!v) { setImportFile(null); setImportPreview([]); setImportAllRows([]); setImportResult(null); if (importFileRef.current) importFileRef.current.value = ""; } }}>
        <DialogContent className="rounded-md max-w-3xl" data-testid="emp-import-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl flex items-center gap-2"><Upload size={18}/> Bulk import employees</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div className="flex gap-2 items-center flex-wrap">
              <Button variant="outline" onClick={downloadSample} data-testid="emp-import-sample" className="rounded-md">
                <FileDown size={13} className="mr-1.5"/> Sample CSV
              </Button>
              <input ref={importFileRef} type="file" accept=".csv,text/csv" data-testid="emp-import-file"
                onChange={(e) => onFileChosen(e.target.files?.[0] || null)}
                className="text-xs"/>
            </div>
            <p className="text-[10px] text-muted-foreground">
              Columns: <b>Name, Email, Employee ID, Department, Company, Branch, Phone, Pincode, Active</b>. Department + Company + Branch must exist in masters. Pincode must be 6 digits. Duplicate Employee IDs are skipped.
            </p>
            {importPreview.length > 0 && (
              <div className="border border-border rounded-md overflow-x-auto" data-testid="emp-import-preview">
                <div className="overline text-[10px] px-2 pt-2">Preview · first {importPreview.length} row{importPreview.length===1?"":"s"}</div>
                <table className="w-full text-[11px] mono">
                  <thead>
                    <tr className="border-b border-border">
                      {Object.keys(importPreview[0]).map((h) => <th key={h} className="p-2 text-left overline text-[9px]">{h}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {importPreview.map((row, i) => (
                      <tr key={i} className="border-b border-border">
                        {Object.values(row).map((v, j) => <td key={j} className="p-2">{v || "—"}</td>)}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {importResult && (
              <div className="border border-border rounded-md p-3 space-y-2" data-testid="emp-import-result">
                <div className={`flex items-center gap-2 font-semibold ${importResult.imported > 0 ? "text-emerald-700" : "text-amber-700"}`}>
                  {importResult.imported > 0 ? <CheckCircle2 size={14}/> : <AlertTriangle size={14}/>}
                  Imported {importResult.imported} of {importResult.total_rows} rows · {importResult.error_count} error{importResult.error_count === 1 ? "" : "s"}
                </div>
                {importResult.errors?.length > 0 && (
                  <>
                    <ul className="text-[10px] space-y-0.5 max-h-40 overflow-y-auto" data-testid="emp-import-errors">
                      {importResult.errors.map((e, i) => (
                        <li key={i} className="text-red-800"><b>Row {e.row}</b> ({e.employee_id || e.name || "—"}): {e.errors.join(" · ")}</li>
                      ))}
                    </ul>
                    <Button variant="outline" onClick={downloadErrorsCsv}
                      data-testid="emp-import-errors-download" className="rounded-md mt-1">
                      <FileDown size={13} className="mr-1.5"/> Download errors as CSV
                    </Button>
                  </>
                )}
              </div>
            )}
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outline" onClick={() => setImportOpen(false)} className="rounded-md" data-testid="emp-import-cancel">Cancel</Button>
              <Button onClick={doImport} disabled={!importFile || importBusy}
                className="rounded-md bg-primary text-primary-foreground" data-testid="emp-import-submit">
                <Upload size={13} className="mr-1"/> {importBusy ? "Importing…" : "Import"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function F({ label, v, onChange, type="text", required, t }) {
  return (
    <label className="block">
      <span className="overline">{label}{required && <span className="text-primary"> *</span>}</span>
      <Input data-testid={t} value={v||""} onChange={e=>onChange(e.target.value)} type={type} required={required}
        className="mt-1 rounded-md border-input focus-visible:ring-primary" />
    </label>
  );
}
